import './Legions.scss'

import { useEffect, useRef, useState } from 'react'
import { Wizard } from '../../../utilities/legions'
import { timeSlots as legionsTimeSlots } from '../../../utilities/constants'
import { useCharacters } from '../../../contexts/Characters'
import useFormBuilder from '../../../hooks/FormBuilderHook'
import Validators from '../../../forms/Validators'
import Select from '../../forms/Select/Select'
import Range from '../../forms/Range/Range'
import Input from '../../forms/Input/Input'

const Legions = () => {
    const characters = useCharacters()
    const [allianceCharacters, setAllianceCharacters] = useState([])
    const [visibleAllianceCharacters, setVisibleAllianceCharacters] = useState([])
    const [charactersVisible, setCharactersVisible] = useState(false)
    const [legions, setLegions] = useState()
    const [legionsWizardResult, setLegionsWizardResult] = useState()
    const [charactersAvailabilities, setCharactersAvailabilities] = useState()
    const [timeSlots, setTimeSlots] = useState(legionsTimeSlots.map((timeSlot) => ({ name: timeSlot })))
    const [searchedLabel, setSearchedLabel] = useState()
    const timeSlotsDays = [timeSlots.slice(0, 3), timeSlots.slice(3)]

    const algorithmPriorityOptionsRef = useRef([
        { value: 'lowerAvailability', label: 'Lower availability (recommended)' },
        { value: 'higherAvailability', label: 'Higher availability' },
    ])

    const groupStrategyOptionsRef = useRef([
        { value: 'maximizeParticipation', label: 'Maximizing participation of characters' },
        { value: 'minimizeIncompatibilities', label: 'Minimizing incompatibile time slots' },
    ])

    const fillStrategyOptionsRef = useRef([
        { value: 'noAvailability', label: 'Characters with no specified time slot' },
        { value: 'unmatchedAvailability', label: 'Characters with unmatched time slots' }
    ])

    const createLegionsRef = useRef() 
    createLegionsRef.current = () => {
        const options = form.$state.value
        options.disabledTimeSlots = timeSlots.filter((timeSlot) => timeSlot.disabled).map((timeSlot) => timeSlot.name)
        const wizard = new Wizard(allianceCharacters.filter((character) => !character.excluded), options)
        setLegionsWizardResult(wizard.createLegions())
    }

    const form = useFormBuilder({ formName: 'legions', onSubmit: () => createLegionsRef.current(), controls: {
        lowPopularityThreshold: { validators: [Validators.required], value: 10 },
        algorithmPriority: { validators: [Validators.required], value: 'lowerAvailability' },
        groupStrategy: { validators: [Validators.required], value: 'maximizeParticipation' },
        fillStrategy: { validators: [Validators.required], value: 'noAvailability' },
    } })

    const toggleTimeSlot = (timeSlotName) => {
        setTimeSlots((prev) => prev.map((timeSlot) => timeSlot.name === timeSlotName ? { ...timeSlot, disabled: !timeSlot.disabled } : timeSlot))
    }

    const toggleCharacterTimeSlot = (editingCharacter, timeSlotName) => {
        setAllianceCharacters(allianceCharacters.map((character) => {
            if (character !== editingCharacter) return character
            else {
                if (!character.availabilitiesOverridden) {
                    character.originalAvailabilities = character.availabilities
                    character.availabilitiesOverridden = true
                }
                return { 
                    ...character,
                    availabilities: character.availabilities.includes(timeSlotName) 
                        ? character.availabilities.filter((availability) => availability !== timeSlotName) 
                        : [...character.availabilities, timeSlotName]
                }
            }
        }))
    }

    const resetCharacterAvailabilities = (resettingCharacter) => {
        setAllianceCharacters(allianceCharacters.map((character) => character !== resettingCharacter ? character : ({
            ...character,
            availabilities: character.originalAvailabilities,
            availabilitiesOverridden: false 
        }) ))
    }
    
    const toggleCharacter = (togglingCharacter) => {
        setAllianceCharacters(allianceCharacters.map((character) => character !== togglingCharacter ? character : { ...character, excluded: !character.excluded } ))
    }

    const filterAllianceCharacters = () => {
        setVisibleAllianceCharacters(allianceCharacters.filter((character) => !searchedLabel || character.name.toLowerCase().includes(searchedLabel.toLowerCase())))
    }

    useEffect(() => {
        filterAllianceCharacters()
    }, [searchedLabel])

    useEffect(() => {
        if (allianceCharacters) {
            const availabilityCount = Object.fromEntries(timeSlots.map((timeSlot) => [timeSlot.name, 0]))
            allianceCharacters.forEach((character) => {
                character.availabilities.forEach(availability => {
                    if (availabilityCount[availability] !== undefined && !character.excluded) availabilityCount[availability]++
                })
            })
            const sortedAvailabilities = Object.fromEntries(Object.entries(availabilityCount).sort((availability1, availability2) => availability2[1] - availability1[1]))
    
            setCharactersAvailabilities(sortedAvailabilities)
            filterAllianceCharacters()
        }
    }, [allianceCharacters])

    useEffect(() => {
        setAllianceCharacters(characters.$state.alliance?.accepted)
    }, [characters.$state.alliance])

    return (
        <div className="container-fluid legions">
                <header className="row mb-4">
                    {charactersAvailabilities && (
                        <div className="time-slots-configuration col-md-auto mb-3">
                            {timeSlotsDays.map((timeSlotDay, index) => (
                                <ul className="time-slots" key={index}>
                                    {timeSlotDay.map((timeSlot) => (
                                        <li className="time-slot" onClick={() => toggleTimeSlot(timeSlot.name)} key={timeSlot.name}>
                                            <span className={`chip ${timeSlot.disabled ? 'base' : 'primary'} mr-3 mb-3`}><b>{timeSlot.name}</b><br/>{charactersAvailabilities[timeSlot.name]} chars</span>
                                        </li>
                                    ))}
                                </ul>
                            ))}
                        </div>
                    )}

                    <div className="col-md-auto mb-3">
                        <Range control={form.$state.controls.lowPopularityThreshold} min={1} max={20} label="Low popularity threshold" />
                    </div>

                    <div className="col-12 ">
                        <div className="card">
                            <header className="row align-items-center">
                                <h3 className="col-auto mb-3"><button className={`${charactersVisible ? 'primary' : 'base dark'} medium`} onClick={() => setCharactersVisible(!charactersVisible)}>{charactersVisible ? 'Hide' : 'Show'} Characters</button></h3>
                                <div className="col-auto mb-3">
                                    <Input onChange={setSearchedLabel} placeholder="Filter" />
                                </div>
                            </header>

                            {charactersVisible && (
                                <ul className="row characters-selection my-3 mr-3 pr-3">
                                    {visibleAllianceCharacters.map((character) => (
                                        <li className="col-lg-6 col-xl-4 mb-3 pr-0" key={character.name}>
                                            <div className={`card character ${character.excluded ? 'excluded' : 'selected'} p-3`}>
                                                <div className="row">
                                                    <div className="col-sm mb-2 mb-sm-0">
                                                        <button className={`card ${character.excluded ? 'semi-dark' : 'primary'} w-100 h-100`} onClick={() => toggleCharacter(character)}>
                                                            <p>{character.name}</p>
                                                        </button>
                                                    </div>

                                                    <div className="col-auto pl-sm-0">
                                                        {timeSlotsDays.map((timeSlotDay, dayIndex) => (
                                                            <ul className="time-slots d-flex" key={dayIndex}>
                                                                {timeSlotDay.map((timeSlot, timeSlotIndex) => (
                                                                    <li className="time-slot" onClick={() => toggleCharacterTimeSlot(character, timeSlot.name)} key={timeSlot.name}>
                                                                        <span className={`chip ${character.availabilities.includes(timeSlot.name) ? 'primary' : 'base'}`}>{timeSlot.name}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ))}
                                                    </div>

                                                    {character.availabilitiesOverridden && (
                                                        <div className="col-auto pl-0">
                                                            <button className="primary small icon outline h-100" onClick={() => resetCharacterAvailabilities(character)}>
                                                                <img src="images/icons/arrow_return.svg" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </header>

            {form.$state && (
                <form className="row align-items-end" onSubmit={form.$state.onSubmit}>
                    <div className="col-xl mb-3">
                        <Select control={form.$state.controls.algorithmPriority} options={algorithmPriorityOptionsRef.current} label="Prioritize adding characters with" />
                    </div>

                    <div className="col-xl mb-3">
                        <Select control={form.$state.controls.groupStrategy} options={groupStrategyOptionsRef.current} label="Prioritize legions creation by" />
                    </div>

                    <div className="col-xl mb-3">
                        <Select control={form.$state.controls.fillStrategy} options={fillStrategyOptionsRef.current} label="Start filling legions missing spots with" />
                    </div>

                    <div className="col-12 d-flex justify-content-end mt-4">
                        <button type="submit" className="primary medium" disabled={!allianceCharacters}>Create Legions</button>
                    </div>
                </form>
            )}

            <hr className="my-4" />

            <div className="row">
                {legionsWizardResult && legionsWizardResult.legions.map((legion, index) => (
                    <div className="col-lg legion" key={index}>
                        <header className="text-center mb-5">
                            <h3 className="mb-1">Legion {index + 1}</h3>
                            <p className="mb-3"><b>{legion.timeSlot}</b></p>
                            <p>Total: <b>{legion.characters.length}</b></p>
                            <p>Matched: <b>{legion.characters.filter((character) => character.availabilities.includes(legion.timeSlot)).length}</b></p>
                            <p>Filled: <b>{legion.characters.filter((character) => !character.availabilities.includes(legion.timeSlot)).length}</b></p>
                        </header>


                        <ul>
                            {legion.characters.map((character) => (
                                <li className={`card ${character.availabilities.includes(legion.timeSlot) ? '' :  'warn'} character mb-3 p-3`} key={character.name}>
                                    <h4>{character.name || 'Character Name'}</h4>
                                    <ul className="availabilities d-flex flex-wrap">
                                        {character.availabilities.map((availability) => (
                                            <li className={`chip availability ${availability === legion.timeSlot ? 'primary' : 'base'} ${availability.toLowerCase().split(' ')[0]} mr-2 mt-2`} key={availability}>{availability}</li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Legions